import { useCurrentMeeting } from "../../contexts/currentMeeting";
import { useSettings } from "conference/components/meeting-page/tools";
import { useChattoHttpMutation } from "conference/apollo-client/chatto/hooks";
import { JoinToChatMutation, JoinToChatDocument, JoinToChatMutationVariables } from "../queries-chatto.generated";
import { SessionContext, SipClient, SipClientHooks, SipSettings, useMediaDevices } from "@dvrt/web-phone-client";
import { useEffect, useRef, useState } from "react";
import { getInvitationIdentifier } from "conference/components/joinMeetingTools";
import { useDispatch } from "react-redux";
import { NotificationVariant } from "conference/types";
import { showNotification } from "conference/actions";
import { useRoomSettings } from "../context";
import { browserWithVersion, difference } from "conference/tools";
import merge from "lodash.merge";
import { useVideoEffectManager } from "../../contexts/videoEffectContext";
import { clearPin, clearTileFitted, setPin, setTileFitted } from "../types";
import { MessageDescriptor } from "react-intl";

export type ChatConnectionState = "connecting" | "connected" | "error";

export function useChatConnector() {
    const currentMeeting = useCurrentMeeting();
    const [connectionState, setConnectionState] = useState<ChatConnectionState>("connecting");
    const { displayName } = useSettings();

    useChattoHttpMutation<JoinToChatMutation, JoinToChatMutationVariables>(JoinToChatDocument, {
        variables: {
            participantUri: currentMeeting.selfUri,
            roomId: currentMeeting.chatRoomId,
            displayName,
        },
        onError: (error) => {
            console.error(error);
            setConnectionState("error");
        },
        onCompleted: (data) => {
            if (data.joinToGroupChat.result) setConnectionState("connected");
        },
        executeOnMount: true,
    });

    return connectionState;
}

export function useStopOnPageUnload(client: SipClient) {
    useEffect(() => {
        window.addEventListener("unload", (_ev) => {
            client.stop();
        });
    }, []);
}

export function useNewSipClient() {
    const currentMeeting = useCurrentMeeting();
    const mediaDevicesHandler = useMediaDevices();
    const identifier = getInvitationIdentifier(currentMeeting.invitations);
    const dispatch = useDispatch();
    const meetingPageSettings = useSettings();
    const videoEffectManager = useVideoEffectManager();

    const sipSettings: SipSettings = {
        userAgent: `Deverto Conference/${browserWithVersion}`,
        proxyUrl: window.tequet.proxyUrl,
        register: false,
        auth: {
            ...currentMeeting.selfSipUserAuth,
            displayName: meetingPageSettings.displayName,
        },
    };

    const hooks: SipClientHooks = {
        notification: (msg: MessageDescriptor, variant: NotificationVariant) => {
            dispatch(showNotification({ message: msg, variant }));
        },
        beforeApplyCallOptions: (options) => {
            merge(options, {
                rtcConstraints: {
                    optional: [{ DtlsSrtpKeyAgreement: false }],
                },
            });
            merge(options, window.callOptionsOverrides);
        },
        getVideoConstraints: (constraints) => {
            merge(constraints, window.videoConstraintsOverrides);
        },
        onMediaStreamCreated: async (stream) => {
            await videoEffectManager.applyEffects(stream);
        },
    };

    return {
        client: new SipClient(sipSettings, mediaDevicesHandler, hooks),
        identifier,
    };
}

export function useDesktopShareChanges(
    context: SessionContext,
    onAdded: (cname: string) => void,
    onRemoved: (cname: string) => void,
) {
    const desktopSharesNames = useRef<Set<string>>(new Set());
    const desktopShares = context.participants.filter((p) => p.screen);

    useEffect(() => {
        const newDesktopSharesNames = new Set(desktopShares.map((p) => p.cname));

        if (newDesktopSharesNames.size > desktopSharesNames.current.size)
            onAdded(difference(newDesktopSharesNames, desktopSharesNames.current).values().next().value);

        if (newDesktopSharesNames.size < desktopSharesNames.current.size)
            onRemoved(difference(desktopSharesNames.current, newDesktopSharesNames).values().next().value);

        desktopSharesNames.current = newDesktopSharesNames;
    }, [desktopShares.length]);
}

export function useDesktopShareAutoPin(context: SessionContext) {
    const roomSettings = useRoomSettings();

    const onAdded = (cname: string) => {
        setPin(roomSettings, cname, true);
        setTileFitted(roomSettings, cname, true);
    };

    const onRemoved = (cname: string) => {
        clearPin(roomSettings, cname, true);
        clearTileFitted(roomSettings, cname, true);
    };

    useDesktopShareChanges(context, onAdded, onRemoved);
}
